import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapFinanceReceivableOrderLocateResult,
  mapFinanceReceivableProjectSearchParams,
  mapFinanceReceivableProjectSearchResult,
  mapFinanceReceivableSummary,
  type FinanceReceivableOrderLocateRaw,
  type FinanceReceivableProjectListRaw,
  type FinanceReceivableSummaryRaw,
} from "../api/adapters";
import type {
  FinanceReceivableOrderLocateItem,
  FinanceReceivableProjectSearchResult,
  FinanceReceivableProjectSearchScope,
  FinanceReceivableSummary,
} from "../types";

/** 调定位接口；团编码与项目 ID 只给一个。 */
async function locateFinanceReceivableOrders(
  params: { team_id: string } | { project_id: string },
): Promise<FinanceReceivableOrderLocateItem[]> {
  const response = await httpClient.get<ApiEnvelope<FinanceReceivableOrderLocateRaw>>("/finance-receivables/orders", {
    params,
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("应收订单定位接口返回了无效数据");
  }
  return mapFinanceReceivableOrderLocateResult(response.data.data);
}

/** 按团编码精确定位订单；团编码不存在时返回空数组。 */
export function locateFinanceReceivableOrdersByTeamId(teamId: string): Promise<FinanceReceivableOrderLocateItem[]> {
  return locateFinanceReceivableOrders({ team_id: teamId });
}

/** 列出一个定制项目的订单；项目还没有交接单时返回空数组。 */
export function locateFinanceReceivableOrdersByProjectId(
  projectId: string,
): Promise<FinanceReceivableOrderLocateItem[]> {
  return locateFinanceReceivableOrders({ project_id: projectId });
}

/** 按学校、客户或项目名称搜定制项目，复用定制项目列表接口。 */
export async function searchFinanceReceivableProjects(
  keyword: string,
  scope: FinanceReceivableProjectSearchScope,
): Promise<FinanceReceivableProjectSearchResult> {
  const response = await httpClient.get<ApiEnvelope<FinanceReceivableProjectListRaw>>("/customization/projects", {
    params: mapFinanceReceivableProjectSearchParams(keyword, scope),
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目列表接口返回了无效数据");
  }
  return mapFinanceReceivableProjectSearchResult(response.data.data);
}

/** 按订单 ID 读取这个团的应收汇总。 */
export async function fetchFinanceReceivableSummary(orderId: string): Promise<FinanceReceivableSummary> {
  const response = await httpClient.get<ApiEnvelope<FinanceReceivableSummaryRaw>>(
    `/finance-receivables/orders/${encodeURIComponent(orderId)}/summary`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("应收汇总接口返回了无效数据");
  }
  return mapFinanceReceivableSummary(response.data.data);
}
